import React from 'react';
import { downloadJsonById, ExportJSONStatus, ExportJSONTypes } from '@atlassian/temp-proxies';
import { EventAttributes } from '@atlassiansox/analytics-web-client';
import { AnalyticsTrackAction, sendAnalyticsEvent } from '@atlassian/temp-analytics';
import { errorNotificationBuilder, NotificationTypes, successNotificationBuilder, useNotification } from '@css-xp/base';
import { failureMessage } from '@css-xp/ppc-common';
import { downloadJSONFromBase64, removeAllSearchParams } from '@css-xp/ppc-utils';

export interface AnalyticsForDownloadedAction {
  trackSubject: string;
  attributes?: EventAttributes;
}

export type useExportJSONTypes = {
  type: ExportJSONTypes;
  id: string;
  transactionAccountId: string;
  isJSONAvailable?: boolean;
  analyticsForDownloadedAction: AnalyticsForDownloadedAction;
};

export const useExportJSON = (data: useExportJSONTypes) => {
  const { type, id, transactionAccountId, isJSONAvailable, analyticsForDownloadedAction } = data;
  const [loading, setLoading] = React.useState(false);
  const [isCompleted, setIsCompleted] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const { notificationSent, setNotificationSent, sendErrorNotification, sendSuccessNotification } = useNotification();

  const downloadJSON = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await downloadJsonById({ type, id, transactionAccountId });
      if (res.data) {
        downloadJSONFromBase64(res.data, `${type}_${id}.json`);
        sendAnalyticsEvent(AnalyticsTrackAction.DOWNLOADED, analyticsForDownloadedAction.trackSubject, {
          transactionAccountId,
          ...analyticsForDownloadedAction?.attributes,
        });
      } else if (res.status === ExportJSONStatus.ACCEPTED) {
        //Show success notification if the downlaod request is accepted.
        if (!notificationSent) {
          const notificationOptions = successNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withHeader(`We’re preparing the JSON file`)
            .withMessage(`We’ll send you a notification when your download is ready.`)
            .withDismissAfter(5000)
            .build();
          sendSuccessNotification(notificationOptions);
          setNotificationSent(true);
        }
      }
      setLoading(false);
      setIsCompleted(true);
      removeAllSearchParams();
    } catch (err) {
      setLoading(false);
      setIsError(true);
      //Show failure notification if the downlaod request is failed.
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader(`We’re unable to ${isJSONAvailable ? 'download' : 'generate'} the JSON file`)
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
    }
  }, [isJSONAvailable]);

  return { downloadJSON, loading, isCompleted, isError };
};
